/* The open record's on-surface state + action row — "Out of the Menus" Part 1
   (designs/navigation-redesign.md NR2–NR5). Replaces the File ▾ menu at a
   project's heading, dev surface only (Navigator gates on
   the nav-redesign feature): the pipeline stepper, the one natural next action
   as a primary button, a quieter secondary, and a single ⋯ overflow holding
   the File menu's remaining verbs, relocated. WHICH verb goes where is the
   shared pure mapping (src/shared/record-actions.ts) — this component only
   renders it and calls the store's EXISTING actions, so every transition keeps
   its cost quote, confirmation, and 409 path byte-for-byte (NR4). One action
   surface, at the heading, above the area tabs — never per tab (RC28). */
import { Fragment, type JSX } from "react";
import type { Project } from "../../../shared/projects";
import {
  overflowTransitions,
  pipelineSteps,
  primaryAction,
  secondaryAction,
  type RecordTransition,
} from "../../../shared/record-actions";
import { useAppState } from "./store";
import { opTitle } from "./helpText";

/* the overflow opens through the same one-menu-at-a-time state as the File
   menu (N13), so the Escape stack and click-to-close behavior are inherited */
const MORE = "More actions";

export function RecordHeader({ project }: { project: Project }): JSX.Element {
  const { state, store } = useAppState();
  const surface = state.me.surface;
  const kind = "project" as const;
  const recordState = project.recordState;

  const runTransition = (t: RecordTransition): void =>
    void store.setRecordState(project, t.to, t.verb);
  const edit = (): void => store.openForm(project.id);

  const primary = primaryAction(recordState, kind);
  const secondary = secondaryAction(recordState, kind);

  /* the overflow: the File menu's verb list, relocated and demoted (NR5) —
     Edit… when the secondary already is Edit's promoted form is omitted,
     the leftover transitions, the section verbs, and the isolated Delete… */
  const ops: { label: string; run: () => void }[] = [
    ...(secondary.kind === "transition" ? [{ label: "Edit…", run: edit }] : []),
    ...overflowTransitions(recordState, kind).map((t) => ({
      label: t.label,
      run: (): void => runTransition(t),
    })),
    { label: "New draft…", run: (): void => store.openForm(null) },
    /* no View… here since Part 2: Close lands on the persistent table — the
       list is one click away, not behind a modal (NR8) */
    { label: "Close", run: (): void => store.closeProject() },
    { label: "Delete…", run: (): void => void store.deleteProject(project) },
  ];

  const steps = pipelineSteps(recordState);
  const open = state.openMenu === MORE;

  return (
    <>
      <div className="nav-stepper" aria-label="Record pipeline">
        {steps.map((st, i) => (
          <Fragment key={st.state}>
            {i > 0 && (
              <span
                className={"step-line" + (steps[i - 1]?.status === "done" ? " done" : "")}
                aria-hidden="true"
              />
            )}
            <span
              className={`step ${st.status}`}
              aria-current={st.status === "current" ? "step" : undefined}
            >
              <span className="step-bub" aria-hidden="true">
                {st.status === "done" ? "✓" : i + 1}
              </span>
              {st.state}
            </span>
          </Fragment>
        ))}
      </div>
      <div className="record-actions">
        <button
          className="btn primary record-primary"
          title={opTitle(surface, primary.label)}
          onClick={() => runTransition(primary)}
        >
          {primary.label}
        </button>
        <button
          className="btn ghost record-secondary"
          title={opTitle(surface, secondary.label)}
          onClick={() => (secondary.kind === "edit" ? edit() : runTransition(secondary))}
        >
          {secondary.label}
        </button>
        <div className="nav-menu-wrap">
          <button
            className={"record-more" + (open ? " open" : "")}
            aria-label={MORE}
            aria-haspopup="menu"
            aria-controls="nav-menu-panel"
            aria-expanded={open}
            onClick={() => store.toggleMenuBar(MORE)}
          >
            ⋯
          </button>
          {open && (
            <div className="nav-menu-panel" id="nav-menu-panel" aria-label={MORE}>
              {ops.map((o) => (
                <button
                  key={o.label}
                  className="nav-menu-item"
                  title={opTitle(surface, o.label)}
                  onClick={() => {
                    store.toggleMenuBar(MORE); // the menu has served its purpose
                    o.run();
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
